import { Button, Input, NumberInput, Select } from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconCar,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { notification } from "antd";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function AddInventoryCreatedListPage() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);
  const [inventoryCreatedData, setInventoryCreatedData] = useState({
    company: localStorage.getItem("companyId"),
    name: "",
    remarks: "",
    deliveryPerson: "",
    licensePlateNumber: "",
    warehouseStaffId: null,
  });
  const [employeeData, setEmployeeData] = useState([]);
  const [tempProductData, setTempProductData] = useState([]);
  const [tempProductInput, setTempProductInput] = useState([]);
  const [tempWarehouseData, setTempWarehouseData] = useState([]);
  const [tempWarehouseInput, setTempWarehouseInput] = useState([]);
  const [tempAreaInput, setTempAreaInput] = useState([]);
  const [tempStorageLocationInput, setTempStorageLocationInput] = useState([]);
  const [tempAmount, setTempAmount] = useState([]);
  const [error, setError] = useState({
    general: {
      isError: false,
      errorMessage: "",
    },
  });
  const [tempProductList, setTempProductList] = useState([]);
  const [productError, setProductError] = useState([]);
  const [storageLocationError, setStorageLocationError] = useState([]);
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const parseId = (value) =>
    typeof value === "string"
      ? value.match(/ID:\s*([a-zA-Z0-9-]+)/)?.[1] ?? null
      : null;

  const isProductInputValid = (value) => {
    const id = parseId(value);
    return !!id && tempProductData.some((p) => p?.id == id);
  };

  const isStorageLocationInputValid = (value) => {
    const id = parseId(value);
    return (
      !!id &&
      tempWarehouseData.some((w) =>
        (w?.storageLocation ?? []).some((l) => l?.id == id)
      )
    );
  };

  const validateProductInput = (index) => {
    setProductError((prev) => {
      const updated = [...prev];
      const value = tempProductInput[index];
      updated[index] = !!value && !isProductInputValid(value);
      return updated;
    });
  };

  const validateStorageLocationInput = (index) => {
    setStorageLocationError((prev) => {
      const updated = [...prev];
      const value = tempStorageLocationInput[index];
      updated[index] = !!value && !isStorageLocationInputValid(value);
      return updated;
    });
  };

  const handleSubmit = async (inventoryCreatedData, tempProductList) => {
    if (tempProductList?.length <= 0) {
      setError((prev) => ({
        ...prev,
        general: {
          isError: true,
          errorMessage: t("warehouse.productListMinOne"),
        },
      }));
      return;
    }

    const normalizedProductList = tempProductList.map((row, i) => {
      let product = row?.product;
      if (!product?.id) {
        const productId = parseId(tempProductInput[i]);
        product = tempProductData.find((p) => p?.id == productId) || product;
      }

      let storageLocation = row?.storageLocation;
      if (!storageLocation) {
        storageLocation = parseId(tempStorageLocationInput[i]);
      }

      const amount = row?.amount ?? tempAmount[i];

      return { ...row, product, storageLocation, amount };
    });

    const hasInvalidItems = normalizedProductList.some(
      (item) => !item?.product?.id || !item?.storageLocation || !item?.amount
    );

    if (hasInvalidItems) {
      setError((prev) => ({
        ...prev,
        general: {
          isError: true,
          errorMessage: t("warehouse.ensureProductComplete"),
        },
      }));
      return;
    }

    setError((prev) => ({
      ...prev,
      general: {
        isError: false,
        errorMessage: "",
      },
    }));
    try {
      const { company, ...stockReceiptData } = inventoryCreatedData;
      if (!stockReceiptData.warehouseStaffId) {
        stockReceiptData.warehouseStaffId = null;
      }
      const result = await single(
        orpcClient.stockReceipt.create([stockReceiptData])
      );
      if (result?.id) {
        const batchResults = await orpcClient.stockReceiptProduct.create(
          normalizedProductList.map((data) => ({
            amount: data?.amount,
            productId: data?.product?.id || null,
            storageLocationId: data?.storageLocation || null,
            stockReceiptId: result?.id,
          }))
        );
        const allSuccessful = batchResults.every((res) => res.success);
        if (allSuccessful) {
          setCurrentStage(1);
        } else {
          console.warn("Some batch operations failed:", batchResults);
        }
      } else {
        api.error({
          message: `Notification `,
          description: <div>Failed to create stock receipt</div>,
          placement: "topLeft",
          duration: 0,
        });
      }
    } catch (error) {
      console.error("Create stock receipt error", error);
      api.error({
        message: `Notification `,
        description: <div>{String(error)}</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
  };

  const handleDataChange = (name, value) => {
    setInventoryCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDeleteProductList = (index) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempWarehouseInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAreaInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempStorageLocationInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setProductError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setStorageLocationError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);
  };

  const handleAddProductList = () => {
    setTempProductList((prev) => [...prev, []]);
  };

  const handleDisplaySelectMembers = (data) => {
    return data?.map((t) => ({
      value: t?.id,
      label: t?.name,
    }));
  };
  const handleDisplaySelectWarehouse = (data) => {
    return data?.map((t) => ({ value: t?.id, label: t?.name }));
  };
  const handleDisplaySelectArea = (warehouseId, warehouseData) => {
    if (!warehouseId) return [];

    const selectedData = warehouseData.find((e) => e?.id == warehouseId);

    return (selectedData?.areas ?? []).map((area) => ({
      value: area?.id,
      label: area?.name,
    }));
  };
  const handleDisplaySelectStorageLocation = (
    warehouseId,
    areaId,
    warehouseData
  ) => {
    if (!warehouseId || !areaId) return [];

    const selectedWarehouse = warehouseData.find((e) => e?.id == warehouseId);
    const selectedArea = (selectedWarehouse?.areas ?? []).find(
      (a) => a?.id == areaId
    );

    return (selectedArea?.storageLocation ?? []).map((location) => ({
      value: location?.id,
      label: location?.name,
    }));
  };

  const handleOnChangeData = async (value, field) => {
    //let result = await apiUserGetProducts(field, value);
    //setTempProductData(result?.data);
  };

  const handleOnOptionSubmit = (type, e, data, index) => {
    let selectedData;
    if (type === "product") {
      let match = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let idNumber = match ? match[1] : null;
      selectedData = data.filter((e) => e?.id == idNumber)[0];
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
      setProductError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;

      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
      setStorageLocationError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const {
    // company,
    name,
    remarks,
    deliveryPerson,
    licensePlateNumber,
    warehouseStaffId,
    // contact,
    // storage_locations,
  } = inventoryCreatedData;

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});

        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching products data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.warehouse.list({});

        const warehousesWithLocations = await Promise.all(
          (result ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            const storageLocation = (areas ?? []).flatMap(
              (area) => area?.storageLocation ?? []
            );
            return { ...warehouse, areas: areas ?? [], storageLocation };
          })
        );

        setTempWarehouseData(warehousesWithLocations);
      } catch (error) {
        console.error("Error fetching warehouses data:", error);
      }
    };

    fetchData();
  }, []);

  const fieldWrapper = (
    label: React.ReactNode,
    Icon: React.ComponentType<{ size?: number | string }> | undefined,
    children: React.ReactNode
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {children}
    </div>
  );

  return (
    <>
      {contextHolder}
      {currentStage === 0 ? (
        <div className="min-h-screen p-8">
          <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
            <div>
              <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
                {t("warehouse.createStockReceipt") || "建立入庫單"}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {t("warehouse.stockReceiptManagement") || "入庫單管理"}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconFileText size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.basicInfo") || "基本資訊"}
                </h3>
              </div>
              <div className="p-6">
                {fieldWrapper(
                  t("warehouse.stockReceiptName") || "入庫單名稱",
                  IconPackage,
                  <Input
                    value={name}
                    onChange={({ target: { value } }) =>
                      handleDataChange("name", value)
                    }
                    leftSection={<IconPackage size={16} />}
                  />
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconPackage size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.productList") || "商品清單"}
                </h3>
              </div>
              <div className="space-y-4 p-6">
                {tempProductList.map((_item, i) => {
                  return (
                    <div
                      className="rounded-lg border border-gray-200 p-4"
                      key={i}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">
                          #{i + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteProductList(i)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-500 hover:bg-red-50"
                        >
                          <IconX size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {fieldWrapper(
                          t("warehouse.productName") || "商品名稱",
                          IconPackage,
                          <Select
                            searchable
                            nothingFoundMessage="查無資料"
                            leftSection={<IconPackage size={16} />}
                            data={handleDisplaySelectMembers(tempProductData)}
                            value={tempProductInput[i] || null}
                            onChange={(value) => {
                              setTempProductInput((prev) => {
                                const updated = [...prev];
                                updated[i] = value;
                                return updated;
                              });
                              const selectedData = tempProductData.find(
                                (p) => p?.id == value
                              );
                              setTempProductList((prev) => {
                                const updated = [...prev];
                                updated[i] = {
                                  ...updated[i],
                                  product: selectedData,
                                };
                                return updated;
                              });
                            }}
                          />
                        )}

                        {fieldWrapper(
                          t("warehouse.warehouseLabel") || "倉庫",
                          IconBuildingWarehouse,
                          <Select
                            searchable
                            nothingFoundMessage="查無資料"
                            leftSection={<IconBuildingWarehouse size={16} />}
                            data={handleDisplaySelectWarehouse(
                              tempWarehouseData
                            )}
                            value={tempWarehouseInput[i] || null}
                            onChange={(value) => {
                              setTempWarehouseInput((prev) => {
                                const updated = [...prev];
                                updated[i] = value;
                                return updated;
                              });

                              setTempAreaInput((prev) => {
                                const updated = [...prev];
                                updated[i] = null;
                                return updated;
                              });
                              setTempStorageLocationInput((prev) => {
                                const updated = [...prev];
                                updated[i] = null;
                                return updated;
                              });
                              setTempProductList((prev) => {
                                const updated = [...prev];
                                updated[i] = {
                                  ...updated[i],
                                  storageLocation: null,
                                };
                                return updated;
                              });
                            }}
                          />
                        )}

                        {fieldWrapper(
                          t("warehouse.areaLabel") || "區",
                          IconLayoutGrid,
                          <Select
                            searchable
                            nothingFoundMessage="查無資料"
                            leftSection={<IconLayoutGrid size={16} />}
                            data={handleDisplaySelectArea(
                              tempWarehouseInput[i],
                              tempWarehouseData
                            )}
                            value={tempAreaInput[i] || null}
                            onChange={(value) => {
                              setTempAreaInput((prev) => {
                                const updated = [...prev];
                                updated[i] = value;
                                return updated;
                              });

                              setTempStorageLocationInput((prev) => {
                                const updated = [...prev];
                                updated[i] = null;
                                return updated;
                              });
                              setTempProductList((prev) => {
                                const updated = [...prev];
                                updated[i] = {
                                  ...updated[i],
                                  storageLocation: null,
                                };
                                return updated;
                              });
                            }}
                          />
                        )}

                        {fieldWrapper(
                          t("warehouse.storageLocationLabel") || "儲位",
                          IconMapPin,
                          <Select
                            searchable
                            nothingFoundMessage="查無資料"
                            leftSection={<IconMapPin size={16} />}
                            data={handleDisplaySelectStorageLocation(
                              tempWarehouseInput[i],
                              tempAreaInput[i],
                              tempWarehouseData
                            )}
                            value={tempStorageLocationInput[i] || null}
                            onChange={(value) => {
                              setTempStorageLocationInput((prev) => {
                                const updated = [...prev];
                                updated[i] = value;
                                return updated;
                              });
                              setTempProductList((prev) => {
                                const updated = [...prev];
                                updated[i] = {
                                  ...updated[i],
                                  storageLocation: value,
                                };
                                return updated;
                              });
                            }}
                          />
                        )}

                        {fieldWrapper(
                          t("warehouse.quantity") || "數量",
                          IconStack,
                          <NumberInput
                            leftSection={<IconStack size={16} />}
                            value={tempAmount[i] || ""}
                            onChange={(e) => {
                              setTempProductList((prev) => {
                                const updatedList = [...prev];
                                updatedList[i] = {
                                  ...updatedList[i],
                                  amount: e,
                                };
                                return updatedList;
                              });

                              setTempAmount((prev) => {
                                const updated = [...prev];
                                updated[i] = e;
                                return updated;
                              });
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button
                  variant="default"
                  fullWidth
                  radius="md"
                  onClick={() => handleAddProductList(tempProductList)}
                >
                  {t("warehouse.addProductRow") || "增加商品列"}
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconTruck size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.deliveryInfo") || "交貨資訊"}
                </h3>
              </div>
              <div className="space-y-4 p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {fieldWrapper(
                    t("warehouse.deliveryPerson") || "送貨人員",
                    IconTruck,
                    <Input
                      leftSection={<IconTruck size={16} />}
                      value={deliveryPerson}
                      onChange={(e) =>
                        handleDataChange("deliveryPerson", e.target.value)
                      }
                    />
                  )}
                  {fieldWrapper(
                    t("warehouse.licensePlateNumber") || "送貨車牌",
                    IconCar,
                    <Input
                      leftSection={<IconCar size={16} />}
                      value={licensePlateNumber ?? ""}
                      onChange={(e) =>
                        handleDataChange("licensePlateNumber", e.target.value)
                      }
                    />
                  )}
                  {fieldWrapper(
                    t("warehouse.inboundStaff") || "入庫人員",
                    IconUser,
                    <Select
                      searchable
                      clearable
                      nothingFoundMessage="查無資料"
                      leftSection={<IconUser size={16} />}
                      data={employeeData
                        ?.filter((t) => t?.status !== "resigned")
                        .map((t) => ({ value: t?.id, label: t?.name }))}
                      value={warehouseStaffId || null}
                      onChange={(value) =>
                        handleDataChange("warehouseStaffId", value)
                      }
                    />
                  )}
                </div>
                {fieldWrapper(
                  t("warehouse.remarks") || "備註",
                  IconFileText,
                  <Input
                    leftSection={<IconFileText size={16} />}
                    value={remarks}
                    onChange={(e) => handleDataChange("remarks", e.target.value)}
                  />
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="default"
                fullWidth
                radius="md"
                onClick={() => navigate(`/hrMainPage/inventoryCreated`)}
              >
                {t("warehouse.cancel") || "取消"}
              </Button>

              <Button
                variant="filled"
                color="gray"
                fullWidth
                radius="md"
                onClick={() => handleSubmit(inventoryCreatedData, tempProductList)}
              >
                {t("warehouse.create") || "建立"}
              </Button>
            </div>
            {error.general.isError && (
              <Input.Error>{error.general.errorMessage}</Input.Error>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex h-full items-center justify-center p-14 text-center">
          <div className="w-[487px] rounded-xl bg-white p-8">
            <div style={{ color: "#6F6F6F" }}>
              {t("warehouse.stockReceiptCreatedSuccess") || "建立入庫單成功！"}
            </div>
            <Button
              variant="filled"
              color="gray"
              style={{ width: "100%", margin: "1rem 0" }}
              radius="md"
              onClick={() => navigate("/hrMainPage/inventoryCreated")}
            >
              {t("warehouse.confirm") || "確定"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
export default AddInventoryCreatedListPage;
